import {
  RedoDiscount,
  RedoDiscountConfiguration,
} from "@redotech/redo-merchant-marketing-rpc/schema/discounts/discount-schemas";
import {
  ImageSearchInput,
  ImageSearchResult,
} from "@redotech/redo-merchant-marketing-rpc/schema/product-images/get-product-images";
import { DiscountGroupType } from "@redotech/redo-model/discount";
import { ScreenSize } from "@redotech/redo-model/email-builder";
import {
  GetEmailRecommendedInteractiveProductsParams,
  GetEmailRecommendedStaticVariantsParams,
  GmailAnnotatedEmailRecommendedVariant,
  InteractiveCartProduct,
} from "@redotech/redo-model/email-product-recommendation";
import { EmailBlock, EmailTemplate } from "@redotech/redo-model/email-template";
import { EmailBuilderMetafield } from "@redotech/redo-model/metafield";
import { Provider } from "@redotech/redo-model/order";
import {
  ProductInfo,
  ShopifyGraphQLProduct,
} from "@redotech/redo-model/product";
import { SavedEmailSection } from "@redotech/redo-model/saved-email-section";
import { SpellCheckImageError } from "@redotech/redo-model/spell-check-image-error";
import { Team } from "@redotech/redo-model/team";
import { ShopifyStorefrontClient } from "@redotech/shopify-storefront";
import { UseQueryResult } from "@tanstack/react-query";
import { createContext } from "react";
import { ProductFilterContext } from "../../product-filter/product-filter-context";

/**
 * This context is only accessible in the email builder, not when rendering a production email.
 * Anything that is needed in both the builder AND production email rendering should go in EmailRenderContext instead.
 */
export interface EmailBuilderContext {
  team?: Team;
  template: EmailTemplate;
  storefrontClient: ShopifyStorefrontClient | null;
  screenSize: ScreenSize;
  /**
   * The actual viewport width for ScreenSize.CUSTOM.
   * Only used when screenSize === ScreenSize.CUSTOM for testing responsive behavior.
   * DESKTOP and MOBILE use hardcoded widths (600px and 375px).
   */
  customViewportWidth?: number;
  setScreenSize: (screenSize: ScreenSize) => void;
  setTemplate: (template: EmailTemplate) => void;
  getCollections: (
    searchQuery?: string,
  ) => Promise<{ id: string; name: string }[]>;
  getMetafields: () => Promise<EmailBuilderMetafield[]>;
  getProduct: (
    productId: string,
    provider: Provider,
  ) => Promise<ShopifyGraphQLProduct | null | undefined>;
  searchProducts: ({
    search,
    provider,
    signal,
  }: {
    search: string;
    provider: Provider;
    signal: AbortSignal;
  }) => Promise<ProductInfo[] | undefined>;
  sendTestEmail: (args: {
    emailAddress: string;
    template: EmailTemplate;
    orderNumber?: string;
    signal?: AbortSignal;
  }) => Promise<void>;
  getDiscounts: (groupType?: DiscountGroupType) => Promise<RedoDiscount[]>;
  getDiscount: (id: string) => Promise<RedoDiscount | undefined>;
  createDiscount: ({
    discountConfiguration,
  }: {
    discountConfiguration: RedoDiscountConfiguration;
  }) => Promise<string>;
  updateDiscount: ({
    discountId,
    discountConfiguration,
  }: {
    discountId: string;
    discountConfiguration: RedoDiscountConfiguration;
  }) => Promise<void>;
  deleteDiscount: (id: string) => Promise<void>;
  searchImages: (
    input: ImageSearchInput,
    options?: { signal?: AbortSignal },
  ) => Promise<ImageSearchResult>;
  getStaticVariantRecommendations: (
    params: GetEmailRecommendedStaticVariantsParams,
  ) => Promise<GmailAnnotatedEmailRecommendedVariant[]>;
  getInteractiveProductRecommendations: (
    params: GetEmailRecommendedInteractiveProductsParams,
  ) => Promise<InteractiveCartProduct[]>;
  countBytes: (
    template: EmailTemplate,
    abortSignal?: AbortSignal,
  ) => Promise<number>;
  getSavedEmailSections: () => Promise<SavedEmailSection[]>;
  getSavedEmailSectionById: ({
    sectionId,
    includeArchived,
  }: {
    sectionId: string;
    includeArchived?: boolean;
  }) => Promise<SavedEmailSection | null>;
  createSavedEmailSection: (params: {
    name: string;
    blocks: EmailBlock[];
  }) => Promise<SavedEmailSection>;
  updateSavedEmailSection: (params: {
    sectionId: string;
    updates: { name?: string; blocks?: EmailBlock[] };
  }) => Promise<SavedEmailSection>;
  archiveSavedEmailSection: (
    sectionId: string,
  ) => Promise<{ success: boolean }>;
  useTeamProviders: () => UseQueryResult<
    { default: Provider; available: Provider[] },
    Error
  >;
  productFilterContext: ProductFilterContext | null;
  spellCheckImage?: (imageUrl: string) => Promise<SpellCheckImageError[]>;
}

export const EmailBuilderContext = createContext<
  EmailBuilderContext | undefined
>(undefined);
